import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Box,
} from '@mui/material';
import { Edit, Close } from '@mui/icons-material';
import axios from 'axios';

export default function PositionManager({ positions, apiUrl, onUpdate }) {
  const [modifyDialog, setModifyDialog] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [modifyData, setModifyData] = useState({ stopLoss: '', takeProfit: '' });

  const handleModifyOpen = (position) => {
    setSelectedPosition(position);
    setModifyData({
      stopLoss: position.stop_loss || '',
      takeProfit: position.take_profit || '',
    });
    setModifyDialog(true);
  };

  const handleModifyPosition = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${apiUrl}/orders/positions/${selectedPosition.id}`,
        {
          stop_loss: modifyData.stopLoss ? parseFloat(modifyData.stopLoss) : null,
          take_profit: modifyData.takeProfit ? parseFloat(modifyData.takeProfit) : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModifyDialog(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to modify position:', err);
    }
  };

  const handleClosePosition = async (position) => {
    if (!window.confirm(`Close position for ${position.symbol}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiUrl}/orders/positions/${position.id}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to close position:', err);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Open Positions ({positions.length})
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Symbol</TableCell>
              <TableCell>Lots</TableCell>
              <TableCell>Lot Size</TableCell>
              <TableCell align="right">Entry</TableCell>
              <TableCell align="right">Current</TableCell>
              <TableCell align="right">P&L</TableCell>
              <TableCell>SL/TP</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.map((pos) => {
              const lotSize = pos.lot_size || 1;
              const totalQty = pos.quantity * lotSize;
              return (
                <TableRow key={pos.id}>
                  <TableCell>
                    <Typography fontWeight="bold">{pos.symbol}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {pos.exchange}
                    </Typography>
                  </TableCell>
                  <TableCell>{pos.quantity}</TableCell>
                  <TableCell>
                    <Chip label={lotSize} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">₹{pos.entry_price.toFixed(2)}</TableCell>
                  <TableCell align="right">₹{pos.current_price.toFixed(2)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: pos.unrealized_pnl >= 0 ? 'success.main' : 'error.main' }}
                  >
                    <Typography fontWeight="bold">₹{pos.unrealized_pnl.toFixed(2)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      SL: {pos.stop_loss ? `₹${pos.stop_loss.toFixed(2)}` : 'None'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      TP: {pos.take_profit ? `₹${pos.take_profit.toFixed(2)}` : 'None'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pos.trade_mode.toUpperCase()}
                      size="small"
                      color={pos.trade_mode === 'paper' ? 'default' : 'primary'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleModifyOpen(pos)}
                      title="Modify SL/TP"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleClosePosition(pos)}
                      title="Close Position"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modify Position Dialog */}
      <Dialog open={modifyDialog} onClose={() => setModifyDialog(false)}>
        <DialogTitle>
          Modify Position: {selectedPosition?.symbol}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Stop Loss"
              value={modifyData.stopLoss}
              onChange={(e) => setModifyData({ ...modifyData, stopLoss: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="number"
              label="Take Profit"
              value={modifyData.takeProfit}
              onChange={(e) => setModifyData({ ...modifyData, takeProfit: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModifyDialog(false)}>Cancel</Button>
          <Button onClick={handleModifyPosition} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}